/**
 * Pipeline Network Logo Component
 * SVG logo representing a data pipeline network for Mi9 Nexus branding.
 */

import type { SVGProps } from 'react';

/**
 * Props for the PipelineNetworkLogo component.
 */
interface PipelineNetworkLogoProps extends SVGProps<SVGSVGElement> {
  /** Size of the logo in pixels. */
  size?: number;
  /** Primary color for the logo. */
  color?: string;
}

/**
 * A pipeline network logo with connected nodes representing data flow.
 *
 * @param { PipelineNetworkLogoProps } props Component props.
 * @returns { JSX.Element } The rendered logo SVG.
 */
export const PipelineNetworkLogo = ({
  size = 48,
  className = '',
  color = '#6366f1',
  ...props
}: PipelineNetworkLogoProps) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" className={className} aria-hidden="true" {...props}>
    {/* Connection lines */}
    <line x1="12" y1="12" x2="24" y2="24" stroke={color} strokeWidth="2" opacity="0.4" />
    <line x1="36" y1="12" x2="24" y2="24" stroke={color} strokeWidth="2" opacity="0.4" />
    <line x1="24" y1="24" x2="12" y2="36" stroke={color} strokeWidth="2" opacity="0.4" />
    <line x1="24" y1="24" x2="36" y2="36" stroke={color} strokeWidth="2" opacity="0.4" />

    {/* Background nodes */}
    <circle cx="12" cy="12" r="5" fill={color} opacity="0.3" />
    <circle cx="36" cy="12" r="5" fill={color} opacity="0.3" />
    <circle cx="12" cy="36" r="5" fill={color} opacity="0.3" />
    <circle cx="36" cy="36" r="5" fill={color} opacity="0.3" />

    {/* Center node (main) */}
    <circle cx="24" cy="24" r="8" fill={color} />
    <circle cx="24" cy="24" r="3" fill="white" />

    {/* Edge nodes */}
    <circle cx="12" cy="12" r="5" stroke={color} strokeWidth="2" fill="white" />
    <circle cx="36" cy="12" r="5" stroke={color} strokeWidth="2" fill="white" />
    <circle cx="12" cy="36" r="5" stroke={color} strokeWidth="2" fill="white" />
    <circle cx="36" cy="36" r="5" stroke={color} strokeWidth="2" fill="white" />
  </svg>
);

export default PipelineNetworkLogo;
